import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Pagination,
  CircularProgress,
  Grid,
  TableSortLabel,
  Collapse,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import Layout from '../Layout';

const ResponsaveisPanel = () => {
  const [responsaveis, setResponsaveis] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingResponsavel, setEditingResponsavel] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [paginacao, setPaginacao] = useState({
    pagina_atual: 1,
    total_paginas: 1,
    total_registros: 0,
    registros_por_pagina: 50
  });
  const [filtros, setFiltros] = useState({
    responsavel: ''
  });
  const [ordenacao, setOrdenacao] = useState({
    order_by: 'id',
    order_direction: 'asc'
  });
  const [formData, setFormData] = useState({
    responsavel: '',
  });

  const fetchResponsaveis = useCallback(async (page = 1, customOrdenacao = null) => {
    try {
      setLoading(true);
      
      // Usar ordenação customizada ou a atual
      const ordenacaoAtual = customOrdenacao || ordenacao;
      
      // Construir query string com filtros e ordenação
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        order_by: ordenacaoAtual.order_by,
        order_direction: ordenacaoAtual.order_direction
      });
      
      // Adicionar filtros se preenchidos
      if (filtros.responsavel) params.append('responsavel', filtros.responsavel);
      
      // Usar a rota de responsáveis
      const response = await api.get(`/responsaveis?${params}`);
      
      // Verificar se a resposta tem estrutura de paginação
      if (response.data.dados && response.data.paginacao) {
        setResponsaveis(response.data.dados);
        setPaginacao(response.data.paginacao);
      } else {
        // Fallback para formato simples (sem paginação)
        let dados = [];
        if (Array.isArray(response.data)) {
          dados = response.data;
        } else if (response.data && response.data.responsaveis) {
          dados = response.data.responsaveis;
        } else if (response.data && typeof response.data === 'object') {
          const possibleArray = Object.values(response.data).find(value => Array.isArray(value));
          if (possibleArray) {
            dados = possibleArray;
          }
        }
        
        // Aplicar filtros localmente se não houver paginação no backend
        if (filtros.responsavel) {
          dados = dados.filter(item => 
            item.Responsavel && item.Responsavel.toLowerCase().includes(filtros.responsavel.toLowerCase())
          );
        }
        
        // Aplicar ordenação localmente
        dados.sort((a, b) => {
          let aValue, bValue;
          if (ordenacaoAtual.order_by === 'id') {
            aValue = a.id;
            bValue = b.id;
          } else {
            aValue = a.Responsavel || '';
            bValue = b.Responsavel || '';
          }
          
          if (ordenacaoAtual.order_direction === 'desc') {
            return bValue > aValue ? 1 : -1;
          } else {
            return aValue > bValue ? 1 : -1;
          }
        });
        
        // Simular paginação local
        const totalRegistros = dados.length;
        const registrosPorPagina = 50;
        const totalPaginas = Math.ceil(totalRegistros / registrosPorPagina);
        const inicio = (page - 1) * registrosPorPagina;
        const fim = inicio + registrosPorPagina;
        const dadosPaginados = dados.slice(inicio, fim);
        
        setResponsaveis(dadosPaginados);
        setPaginacao({
          pagina_atual: page,
          total_paginas: totalPaginas,
          total_registros: totalRegistros,
          registros_por_pagina: registrosPorPagina
        });
      }
    } catch (error) {
      console.error('Erro ao buscar responsáveis:', error.response || error);
      setError('Erro ao carregar responsáveis. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filtros, ordenacao]);

  useEffect(() => {
    fetchResponsaveis(1);
  }, [fetchResponsaveis]);

  const handlePageChange = (event, value) => {
    fetchResponsaveis(value);
  };

  const handleSort = (campo) => {
    const novaOrdenacao = {
      order_by: campo,
      order_direction: ordenacao.order_by === campo && ordenacao.order_direction === 'asc' ? 'desc' : 'asc'
    };
    setOrdenacao(novaOrdenacao);
    fetchResponsaveis(1, novaOrdenacao);
  };

  const handleFilterChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const aplicarFiltros = () => {
    fetchResponsaveis(1);
  };

  const limparFiltros = () => {
    setFiltros({
      responsavel: ''
    });
    // Após limpar, buscar novamente
    setTimeout(() => {
      fetchResponsaveis(1);
    }, 100);
  };

  const handleSubmit = async () => {
    try {
      // Transformar os dados para o formato esperado pelo backend
      const dataToSend = {
        Responsavel: formData.responsavel
      };
      
      if (editingResponsavel) {
        await api.put(`/responsaveis/${editingResponsavel.id}`, dataToSend);
        setSuccess('Responsável atualizado com sucesso!');
      } else {
        await api.post('/responsaveis', dataToSend);
        setSuccess('Responsável adicionado com sucesso!');
      }
      
      setOpenDialog(false);
      setEditingResponsavel(null);
      setFormData({ responsavel: '' });
      fetchResponsaveis(paginacao.pagina_atual);
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Erro ao salvar responsável:', error);
      setError('Erro ao salvar. Por favor, verifique os dados e tente novamente.');
    }
  };

  const handleEdit = (responsavel) => {
    setEditingResponsavel(responsavel);
    setFormData({ responsavel: responsavel.Responsavel || '' });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este responsável?')) {
      try {
        await api.delete(`/responsaveis/${id}`);
        setSuccess('Responsável excluído com sucesso!');
        fetchResponsaveis(paginacao.pagina_atual);
        
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } catch (error) {
        console.error('Erro ao excluir responsável:', error);
        setError('Erro ao excluir. Por favor, tente novamente.');
      }
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Gerenciamento de Responsáveis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cadastre e gerencie os responsáveis (comerciais) do sistema
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterIcon />}
            >
              Filtros
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setEditingResponsavel(null);
                setFormData({ responsavel: '' });
                setOpenDialog(true);
              }}
              startIcon={<AddIcon />}
            >
              Novo Responsável
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Painel de Filtros */}
        <Collapse in={showFilters}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filtros de Pesquisa
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Nome do Responsável"
                    value={filtros.responsavel}
                    onChange={(e) => handleFilterChange('responsavel', e.target.value)}
                    placeholder="Digite o nome do responsável"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={aplicarFiltros}
                    sx={{ height: '56px' }}
                  >
                    Aplicar Filtros
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={limparFiltros}
                    startIcon={<ClearIcon />}
                    sx={{ height: '56px' }}
                  >
                    Limpar
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>

        {/* Informações de paginação */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {responsaveis.length} de {paginacao.total_registros} registros
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Página {paginacao.pagina_atual} de {paginacao.total_paginas}
          </Typography>
        </Box>

        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={ordenacao.order_by === 'id'}
                    direction={ordenacao.order_by === 'id' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'Responsavel'}
                    direction={ordenacao.order_by === 'Responsavel' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('Responsavel')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Carregando dados...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : responsaveis.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum registro encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                responsaveis.map((responsavel) => (
                  <TableRow key={responsavel.id}>
                    <TableCell>{responsavel.id}</TableCell>
                    <TableCell>{responsavel.Responsavel}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleEdit(responsavel)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDelete(responsavel.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Componente de Paginação */}
        {paginacao.total_paginas > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={paginacao.total_paginas}
              page={paginacao.pagina_atual}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              disabled={loading}
            />
          </Box>
        )}

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingResponsavel ? 'Editar Responsável' : 'Novo Responsável'}
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Nome do Responsável"
              name="responsavel"
              value={formData.responsavel}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ResponsaveisPanel; 